import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export type LifeCoverageAmount = {
  type?: 'flat_amount' | 'multiple_of_salary';
  flatAmount?: number;
  salaryMultiple?: number;
  maximum?: number;
};

export type LifeRFPParameters = {
  coverageTypes: string[];
  coverageAmounts: LifeCoverageAmount[];
  voluntaryBuyUp: boolean;
};

type LifeRFPFormProps = {
  value: LifeRFPParameters;
  onChange: (value: LifeRFPParameters) => void;
};

export function LifeRFPForm({ value, onChange }: LifeRFPFormProps) {
  const updateField = (field: keyof LifeRFPParameters, newValue: any) => {
    onChange({ ...value, [field]: newValue });
  };

  const toggleCoverageType = (type: string) => {
    const current = value.coverageTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    updateField('coverageTypes', updated);
  };

  const addCoverageRow = () => {
    const amounts = value.coverageAmounts || [];
    updateField('coverageAmounts', [...amounts, {}]);
  };

  const removeCoverageRow = (index: number) => {
    const amounts = [...(value.coverageAmounts || [])];
    amounts.splice(index, 1);
    updateField('coverageAmounts', amounts);
  };

  const updateCoverageRow = (index: number, field: keyof LifeCoverageAmount, newValue: any) => {
    const amounts = [...(value.coverageAmounts || [])];
    if (field === 'type') {
      if (newValue === 'flat_amount') {
        amounts[index] = { type: 'flat_amount', flatAmount: amounts[index].flatAmount };
      } else if (newValue === 'multiple_of_salary') {
        amounts[index] = { type: 'multiple_of_salary', salaryMultiple: amounts[index].salaryMultiple, maximum: amounts[index].maximum };
      }
    } else {
      amounts[index] = { ...amounts[index], [field]: newValue };
    }
    updateField('coverageAmounts', amounts);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CoverageIcon planType="life" size="sm" />
          Life & AD&D
        </CardTitle>
        <p className="text-xs text-muted-foreground">How much cover each employee gets.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Coverage Type */}
        <div className="space-y-3">
          <Label>Coverage Type</Label>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="life-employer"
                checked={value.coverageTypes?.includes('employer_sponsored')}
                onCheckedChange={() => toggleCoverageType('employer_sponsored')}
              />
              <label htmlFor="life-employer" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Employer Sponsored
              </label>
            </div>
          </div>
        </div>

        {/* Coverage Amounts */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Coverage Amounts</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCoverageRow}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Row
            </Button>
          </div>

          {(value.coverageAmounts || [{}]).map((amount, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Coverage Option {index + 1}</span>
                {value.coverageAmounts && value.coverageAmounts.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCoverageRow(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`type-${index}`}>Type</Label>
                  <Select
                    value={amount.type || ''}
                    onValueChange={(val) => updateCoverageRow(index, 'type', val)}
                  >
                    <SelectTrigger id={`type-${index}`}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat_amount">Flat Amount</SelectItem>
                      <SelectItem value="multiple_of_salary">Multiple of Salary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {amount.type === 'flat_amount' && (
                  <div className="space-y-2">
                    <Label htmlFor={`flat-${index}`}>Flat Amount</Label>
                    <Input
                      id={`flat-${index}`}
                      type="number"
                      placeholder="e.g., 100000"
                      value={amount.flatAmount || ''}
                      onChange={(e) => updateCoverageRow(index, 'flatAmount', e.target.value ? parseInt(e.target.value) : undefined)}
                    />
                  </div>
                )}

                {amount.type === 'multiple_of_salary' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor={`multiple-${index}`}>Multiple of Salary</Label>
                      <Input
                        id={`multiple-${index}`}
                        type="number"
                        step="0.5"
                        placeholder="e.g., 3"
                        value={amount.salaryMultiple || ''}
                        onChange={(e) => updateCoverageRow(index, 'salaryMultiple', e.target.value ? parseFloat(e.target.value) : undefined)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`max-${index}`}>Maximum</Label>
                      <Input
                        id={`max-${index}`}
                        type="number"
                        placeholder="e.g., 500000"
                        value={amount.maximum || ''}
                        onChange={(e) => updateCoverageRow(index, 'maximum', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Voluntary Buy-Up */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="voluntary-buyup"
            checked={value.voluntaryBuyUp}
            onCheckedChange={(checked) => updateField('voluntaryBuyUp', checked)}
          />
          <label htmlFor="voluntary-buyup" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Include voluntary buy-up options in quote
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
